import * as React from 'react';
import './Notes.scss';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Dialog from 'src/components/common/Dialog';
import DialogList from 'src/dialogs';
import Form from 'src/components/controls/forms/Form';
import LeaseholdModel from 'src/models/leasehold/Leasehold';
import { FieldArray } from 'react-final-form-arrays';
import FormCheckbox from 'src/components/controls/forms/redesign/FormCheckbox';
import { FormApi, AnyObject } from 'final-form';
import FormLabel from 'src/components/controls/forms/FormLabel';
import FormTextBox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import FormRow from 'src/components/controls/forms/FormRow';
import moment from 'moment';

export interface IManageLeaseholdStoreProps {
    errorMessage: string;
    leaseholds: Array<LeaseholdModel>;
    clientNumber: number;
    projectNumber: number;
    sectionNumber: number;
    tractNumber: number;
    horizonNumber: number;
}

export interface IManageLeaseholdDispatchProps {
    clearError: () => void;
    hideDialog: (dialogName: string) => void;
    updateLeasehold: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        leaseholds: Array<LeaseholdModel>
    ) => void;
}

interface IManageLeaseholdStateProps {
    pageNumber: number;
}

type IManageLeaseholdPageProps = IManageLeaseholdStoreProps & IManageLeaseholdDispatchProps;

class ManageLeaseholdDialog extends React.Component<IManageLeaseholdPageProps, IManageLeaseholdStateProps> {
    private form: FormApi;
    constructor(props: IManageLeaseholdPageProps) {
        super(props);
        this.state = {
            pageNumber: 0,
        };
    }

    exitDialog = () => {
        this.setState({
            pageNumber: 0,
        });
        this.props.clearError();
    };

    updateLeasehold = () => {
        const formValues = this.form.getState().values as AnyObject;
        const leaseholds = formValues.leaseholds;
        const updateArray = new Array<LeaseholdModel>();
        for (let i = 0; i < leaseholds?.length; i++) {
            if (leaseholds[i].isSelected) {
                leaseholds[i].hbp = formValues?.hbpSelected;
                leaseholds[i].hbu = formValues?.hbuSelected;
                leaseholds[i].fieldName = formValues?.fieldName;
                leaseholds[i].unitName = formValues?.unitName;
                leaseholds[i].apiNumber = formValues?.apiNumber;
                leaseholds[i].operator = formValues?.operator;
                leaseholds[i].poolingOrderNumber = formValues?.poolingOrderNumber;
                leaseholds[i].wellName = formValues?.wellName;
                leaseholds[i].leaseDate = leaseholds[i].leaseDate === '0000-12-31' ? moment().toDate() : leaseholds[i].leaseDate;
                leaseholds[i].effectiveDate = leaseholds[i].effectiveDate === '0000-12-31' ? moment().toDate() : leaseholds[i].effectiveDate;
                leaseholds[i].expirationDate = leaseholds[i].expirationDate === '0000-12-31' ? moment().toDate() : leaseholds[i].expirationDate;
                updateArray.push(leaseholds[i]);
            }
        }
        this.props.updateLeasehold(
            this.props.clientNumber,
            this.props.projectNumber,
            this.props.sectionNumber,
            this.props.tractNumber,
            this.props.horizonNumber,
            updateArray
        );
        this.props.hideDialog(DialogList.MANAGE_LEASEHOLD);
        this.setState({ pageNumber: 0 });
    };

    incrementPage = () => {
        this.setState({ pageNumber: this.state.pageNumber + 1 });
    };
    decrementPage = () => {
        this.setState({ pageNumber: this.state.pageNumber - 1 });
    };

    selectAll = () => {
        const leaseholds = (this.form.getState().values as AnyObject).leaseholds;
        const length: number = leaseholds?.length;
        for (let i = 0; i < length; i++) {
            const tempValue = leaseholds[i];
            tempValue.isSelected = true;
            this.form.mutators.update('leaseholds', i, tempValue);
        }
    };
    deselectAll = () => {
        const leaseholds = (this.form.getState().values as AnyObject).leaseholds;
        const length: number = leaseholds?.length;
        for (let i = 0; i < length; i++) {
            const tempValue = leaseholds[i];
            tempValue.isSelected = false;
            this.form.mutators.update('leaseholds', i, tempValue);
        }
    };
    getUnitName = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        return this.naFiller(lease.unitName);
    };

    getFieldName = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        return this.naFiller(lease.fieldName);
    };

    getWellName = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        return this.naFiller(lease.wellName);
    };

    getApiNo = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        return this.naFiller(lease.apiNumber);
    };

    getPoolingOrderNumber = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        return this.naFiller(lease.poolingOrderNumber);
    };

    getId = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        return this.naFiller(lease.id);
    };

    getHorizonNumber = (leaseIndex: number) => {
        const lease = (this.form.getState().values as AnyObject).leaseholds[leaseIndex];
        if (lease.horizonNumber && lease.tractNumber && lease.sectionNumber) {
            return `${lease.sectionNumber}.${lease.tractNumber}-${lease.horizonNumber}`;
        } else {
            return this.naFiller(undefined);
        }
    };

    naFiller = (value: string) => {
        if (value) {
            return value;
        } else {
            return 'N/A';
        }
    };
    calculateName = (ownerIndex: number): string => {
        const owner = (this.form.getState().values as AnyObject).leaseholds[ownerIndex].mineralOwner;
        if (owner.surfaceOwnerAgentFirstName && owner.surfaceOwnerAgentLastName) {
            return `${owner.surfaceOwnerAgentFirstName} ${owner.surfaceOwnerAgentLastName}`;
        }
        if (owner.regionalOffice) {
            return owner.regionalOffice;
        }
        if (owner.agencyName) {
            return owner.agencyName;
        }
        if (owner.name) {
            return owner.name;
        }
        if (owner.trustName) {
            return owner.trustName;
        }
        if (owner.firstName && owner.lastName) {
            return `${owner.firstName} ${owner.lastName}`;
        }
        return undefined;
    };

    public render() {
        this.props.leaseholds?.forEach((x: AnyObject) => (x.isSelected === undefined ? (x.isSelected = false) : (x = x)));
        return (
            <>
                <Dialog dialogName={DialogList.MANAGE_LEASEHOLD} title={`Manage Leasehold`} onCloseDialog={this.exitDialog}>
                    {this.props.leaseholds && (
                        <div>
                            <div style={{ color: 'red', height: '3rem' }}>{this.props.errorMessage}</div>
                            <Form
                                onSubmit={this.updateLeasehold}
                                initialValues={{ leaseholds: this.props.leaseholds ? this.props.leaseholds : [] }}
                                hasFieldArray={true}
                                onFormCreated={(form) => (this.form = form)}
                            >
                                <div>
                                    {this.state.pageNumber === 0 && this.props.leaseholds?.length > 0 && (
                                        <>
                                            <FormRow>
                                                <div style={{ width: '1rem' }} />
                                                <div className="field-medium">Name</div>
                                                <div className="field-medium">Id</div>
                                                <div className="field-medium">Horizon</div>
                                                <div className="field-medium">HBP</div>
                                                <div className="field-medium">HBU</div>
                                                <div className="field-medium">Unit Name</div>
                                                <div className="field-medium">Field Name</div>
                                                <div className="field-medium">Pooling Order No.</div>
                                                <div className="field-medium">Well Name</div>
                                                <div className="field-medium">API No.</div>
                                            </FormRow>
                                            <div style={{ overflow: 'auto', maxHeight: '40rem' }}>
                                                <FieldArray name="leaseholds">
                                                    {({ fields, meta }) =>
                                                        fields.map((name, index) => (
                                                            <div key={name}>
                                                                <div style={{ minWidth: '130rem' }}>
                                                                    <FormRow>
                                                                        <div style={{ width: '1rem' }}>
                                                                            <FormCheckbox label="" name={`${name}.isSelected`} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <label>
                                                                                <span className="form-text">{this.calculateName(index)}</span>
                                                                            </label>
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getId(index)} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getHorizonNumber(index)} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormCheckbox label="" name={`${name}.hbp`} isDisabled={true} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormCheckbox label="" name={`${name}.hbu`} isDisabled={true} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getUnitName(index)} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getFieldName(index)} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getPoolingOrderNumber(index)} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getWellName(index)} />
                                                                        </div>
                                                                        <div className="field-medium">
                                                                            <FormLabel text={this.getApiNo(index)} />
                                                                        </div>
                                                                    </FormRow>
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                </FieldArray>
                                            </div>
                                            <div className="button-row mt-2 ml-2 mr-2 mb-2">
                                                <div>
                                                    <Button text="Select All" onClick={this.selectAll} intent="primary" />
                                                    <Button text="Deselect All" onClick={this.deselectAll} className="ml-1" intent="primary" />
                                                </div>
                                                <Button text="Next" onClick={this.incrementPage} intent="primary" />
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div>
                                    {this.state.pageNumber === 1 && (
                                        <>
                                            <div style={{ overflow: 'auto', maxHeight: '40rem' }}>
                                                <FormTextBox
                                                    className="lh-half-width"
                                                    label="API No."
                                                    name={`apiNumber`}
                                                    type={FormTextBoxDataType.Int}
                                                />
                                                <FormTextBox className="lh-half-width" label="Operator" name={`operator`} />
                                                <div style={{ display: 'flex' }}>
                                                    <div className="lh-fourth-width ml-1">
                                                        <FormCheckbox label="HBP" name={`hbpSelected`} />
                                                    </div>
                                                    <div className="lh-fourth-width ml-1">
                                                        <FormCheckbox label="HBU" name={`hbuSelected`} />
                                                    </div>
                                                </div>
                                                <FormTextBox className="lh-half-width" label="Pooling Order Number" name={`poolingOrderNumber`} />
                                                <FormTextBox className="lh-half-width" label="Well Name" name={`wellName`} />
                                                <FormTextBox className="lh-half-width" label="Field Name" name={`fieldName`} />
                                                <FormTextBox className="lh-half-width" label="Unit Name" name={`unitName`} />
                                            </div>
                                            <div className="button-row">
                                                <Button className="mt-2 ml-2 mb-2" text="Prev" onClick={this.decrementPage} intent="primary" />
                                                <Button className="mt-2 mr-2 mb-2" text="Save" type="submit" intent="primary" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Form>
                        </div>
                    )}
                    {this.props.leaseholds && this.props.leaseholds?.length === 0 && <span>No Leaseholds attached to this tract</span>}
                </Dialog>
            </>
        );
    }
}

export default ManageLeaseholdDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ManageLeaseholdDialog.tsx